"use client";

import Link from "next/link";
import { ShieldCheck, Globe2, Sparkles, FileText } from "lucide-react";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname, withLocale } from "@/lib/i18n";
import { SectionHeader } from "./SectionHeader";
import { getGrowthHighlights, getGrowthSectionCopy } from "@/lib/growth-copy";

const icons = [ShieldCheck, Globe2, Sparkles, FileText];

export function GrowthContent() {
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const content = getGrowthSectionCopy(locale);
  const highlights = getGrowthHighlights(locale);

  return (
    <section className="container mx-auto px-4 py-12 md:px-6 md:py-20">
      <SectionHeader
        title={content.title}
        description={content.description}
      />

      <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-[36px] border border-positivus-dark bg-positivus-dark p-8 text-white shadow-[0_6px_0_0_#191A23] md:p-10">
          <p className="mb-5 text-sm font-semibold uppercase tracking-[0.2em] text-positivus-green">
            {content.eyebrow}
          </p>
          <h3 className="max-w-2xl text-3xl font-medium leading-tight md:text-4xl">
            {content.headline}
          </h3>
          <div className="mt-6 space-y-4 text-base leading-7 text-white/85">
            <p>{content.paragraphOne}</p>
            <p>{content.paragraphTwo}</p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              href={withLocale(locale, "/tools")}
              className="rounded-[14px] bg-positivus-green px-5 py-3 text-sm font-medium text-positivus-dark transition-colors hover:bg-white"
            >
              {content.explore}
            </Link>
            <Link
              href={withLocale(locale, "/blog")}
              className="rounded-[14px] border border-white/25 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white hover:text-positivus-dark"
            >
              {content.readBlog}
            </Link>
            <Link
              href={withLocale(locale, "/privacy")}
              className="rounded-[14px] border border-white/25 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white hover:text-positivus-dark"
            >
              {content.privacy}
            </Link>
          </div>
        </div>

        <div className="grid gap-5">
          {highlights.map((item, index) => {
            const Icon = icons[index % icons.length];

            return (
              <div
                key={item.title}
                className="rounded-[30px] border border-positivus-dark bg-white p-6 shadow-[0_4px_0_0_#191A23]"
              >
                <div className="mb-4 inline-flex rounded-[16px] bg-positivus-green p-3 text-positivus-dark">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mb-2 text-xl font-medium text-positivus-dark">{item.title}</h3>
                <p className="text-sm leading-6 text-gray-600">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 rounded-[32px] border border-positivus-dark/10 bg-positivus-green/15 p-6 md:p-8">
        <p className="text-lg font-medium text-positivus-dark">
          {content.nextStep}
        </p>
        <p className="mt-2 max-w-4xl text-base leading-7 text-gray-700">
          {content.nextStepBody}
        </p>
      </div>
    </section>
  );
}
